import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthApiService } from 'app/modules/auth/auth.api.service';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';

// Flag para controlar se já existe um refresh em andamento
let isRefreshing = false;
// Subject para armazenar o novo token e notificar as requisições pendentes
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const authApiService = inject(AuthApiService);
    const router = inject(Router);

    // Skip if it's an auth request
    if (req.url.includes('auth') && !req.url.includes('auth-as')) {
        return next(req);
    }

    // Add Authorization header if access token is available
    if (authService.accessToken) {
        const authReq = addTokenToRequest(req, authService.accessToken);
        return next(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    // Se não tiver refresh token ou estiver na página de login, faz logout
                    if (!authService.refreshToken || router.url.includes('sign-in')) {
                        return handleSignOut(authService, router, error);
                    }

                    if (!isRefreshing) {
                        isRefreshing = true;
                        refreshTokenSubject.next(null);

                        return authApiService.renewToken(authService.refreshToken).pipe(
                            switchMap(data => {
                                isRefreshing = false;
                                refreshTokenSubject.next(data.accessToken);
                                
                                authService.accessToken = data.accessToken;
                                authService.refreshToken = data.refreshToken;

                                return next(addTokenToRequest(req, data.accessToken));
                            }),
                            catchError(renewError => {
                                isRefreshing = false;
                                refreshTokenSubject.next(null);
                                return handleSignOut(authService, router, renewError);
                            })
                        );
                    }

                    // Se já existe um refresh em andamento, aguarda o novo token
                    return refreshTokenSubject.pipe(
                        filter(token => token !== null),
                        take(1),
                        switchMap(token => {
                            return next(addTokenToRequest(req, token!));
                        })
                    );
                }
                return throwError(() => error);
            })
        );
    }

    return next(req);
};

function addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
        headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
}

function handleSignOut(authService: AuthService, router: Router, error: any): Observable<never> {
    authService.signOut();
    router.navigate(['/sign-in'], {
        queryParams: { applicationId: authService.lastApplicationId },
    });
    return throwError(() => error);
}